import './StatusIndicator.css';
import { useRef } from 'preact/hooks';
import { AnimatedNumber } from '../../components/AnimatedNumber';
import { DigitRoller } from '../../components/DigitRoller';
import { LiveDot, type LiveDotVariant } from '../../components/LiveDot';
import type { Geography } from '../../contracts/static';
import { type CopyKey, t } from '../../copy/strings';
import { fmtSections } from '../../format/numbers';
import { fmtClockSec, fmtTime } from '../../format/time';
import { epochMs, tMinOfIso } from '../../model/time';
import type { ConnectionState, ResultsView } from '../../model/types';
import { useConfig, useFeed, useFeedFailing, useStatics, useTimeline, useView } from '../../state/hooks';
import { now } from '../../state/store';

// ── Horas de Brasília (§13.4.3) ─────────────────────────────────────────────────────────────────────────────
// Formatadores de src/format/time.ts e t_min de src/model/time.ts (§10.1.1).
/** Minutos desde 00h00 de Brasília (UTC−3 fixo) de `electionDate`; ≥ 1440 = dia seguinte (§10.1.1). */
const tMinOf = (iso: string, electionDate: string): number => tMinOfIso(iso, epochMs(electionDate));

// ── Nota de M30 (§6.4, §6.5) ────────────────────────────────────────────────────────────────────────────────
/**
 * Fração das seções que a soma das UFs já tem, quando os estados estão à frente do total (M30): soma das UFs ÷ seções
 * do Brasil (`sections_ref` de `Geography.places`, 27 UFs + ZZ). Sem a geografia, a razão pela própria reconciliação
 * (nacional apurado = soma − diff). null sem `places_ahead` ou sem base para o cálculo. O número do TSE nunca muda.
 */
export function placesAheadFraction(view: ResultsView | null, geo: Geography | null): number | null {
  const rec = view?.reconciliation;
  if (!view || !rec?.places_ahead) return null;
  const counted = rec.places_sum.sections_counted;
  const total = geo ? geo.places.reduce((sum, p) => sum + p.sections_ref, 0) : 0;
  let f: number | null = null;
  if (total > 0) f = counted / total;
  else {
    const national = counted - rec.diff.sections;
    if (national > 0 && view.national.sectionsFraction > 0) f = (view.national.sectionsFraction * counted) / national;
  }
  return f === null || !Number.isFinite(f) ? null : Math.min(1, Math.max(0, f));
}

// ── Modelo (§6.4 blocos 3 e 4) ──────────────────────────────────────────────────────────────────────────────
export type StatusState =
  | 'connecting'
  | 'live'
  | 'simulation'
  | 'replay_loading'
  | 'replay'
  | 'tse_stalled'
  | 'stalled'
  | 'reconnecting'
  | 'offline'
  /** M1 (ORD-F-148): sem nenhum número desde a abertura, com 3 falhas ou 60 s (só com a trava do aviso ligada). */
  | 'failing';

/** Formas: l = longa (container > 1520), m = média (≤ 1520), s = curta (≤ 1180, celular, TV). */
export type StatusForm = 'l' | 'm' | 's';

export interface StatusSegment {
  kind: 'text' | 'time' | 'pct';
  /** Chave estável do trecho (hora e percentual mantêm a identidade entre estados). */
  key: string;
  /** Texto literal (kind 'text'); vazio nos demais. */
  text: string;
  /** Formas em que o trecho aparece ("lms", "lm", "s"…). */
  forms: string;
}

export interface StatusInput {
  connection: ConnectionState;
  /** timeline.live */
  live: boolean;
  replayLoading: boolean;
  /** Hora pedida no replay (t_min). */
  displayTMin: number;
  view: ResultsView | null;
  /** Relógio da fonte (ms), para "verificado há". */
  nowMs: number;
  /** feed.verifiedAt (ms) ou null. */
  verifiedAtMs: number | null;
  electionDate: string;
  placesAhead: number | null;
  /** M1: o aviso de falha (useFeedFailing); padrão false. */
  failing?: boolean;
}

export interface StatusModel {
  state: StatusState;
  dot: LiveDotVariant;
  /** Estados de alerta: anel vazado e texto em --color-fg-1. */
  alert: boolean;
  timeTMin: number | null;
  /** {HHhMM} formatado. */
  time: string | null;
  /** Fração das seções (AnimatedNumber); null sem percentual. */
  pct: number | null;
  /** Texto final de cada forma (o que a pessoa vê). */
  forms: Record<StatusForm, string>;
  segments: StatusSegment[];
  /** Cópia .sr-only: sempre a forma longa, com " (horário de Brasília)" depois da hora. */
  sr: string;
  /** Dica do mouse (linhas separadas por "\n"); null quando não há. */
  title: string | null;
}

const KEYS: Record<StatusState, readonly [CopyKey, CopyKey, CopyKey]> = {
  connecting: ['status.connecting', 'status.connecting', 'status.connecting'],
  live: ['status.live', 'status.live.medium', 'status.live.short'],
  simulation: ['status.live', 'status.live.medium', 'status.live.short'],
  replay_loading: ['status.replayLoading', 'status.replayLoading', 'status.replayLoading'],
  replay: ['status.replay', 'status.replay.medium', 'status.replay.short'],
  tse_stalled: ['status.tseStalled', 'status.tseStalled.medium', 'status.tseStalled.short'],
  stalled: ['status.stalled', 'status.stalled.medium', 'status.stalled.short'],
  reconnecting: ['status.reconnecting', 'status.reconnecting.medium', 'status.reconnecting.short'],
  offline: ['status.offline', 'status.offline.medium', 'status.offline.short'],
  failing: ['status.failing', 'status.failing.medium', 'status.failing.short'],
};

const ALERT: ReadonlySet<StatusState> = new Set(['tse_stalled', 'stalled', 'reconnecting', 'offline', 'failing']);
/** Os resultados oficiais (M1): o mesmo endereço do aviso sem JavaScript e da guarda do boot. */
export const TSE_RESULTS_URL = 'https://resultados.tse.jus.br/';
const FORMS: readonly StatusForm[] = ['l', 'm', 's'];
/** Marcadores (área de uso privado) para recortar a hora e o percentual de dentro do texto do catálogo. */
const T_MARK = '';
const P_MARK = '';

/** Estado exibido (§6.4 bloco 3): fora do ao vivo, replay; senão, a conexão (sem `view`, "Conectando"). */
export function shownState(
  connection: ConnectionState,
  live: boolean,
  replayLoading: boolean,
  hasView: boolean,
  failing = false,
): StatusState {
  if (!live) return replayLoading ? 'replay_loading' : hasView ? 'replay' : 'connecting';
  if (!hasView && failing) return 'failing';
  if (!hasView || connection === 'connecting') return 'connecting';
  if (connection === 'replay') return 'replay';
  return connection;
}

function dotOf(state: StatusState): LiveDotVariant {
  if (state === 'connecting') return 'waiting';
  if (state === 'live' || state === 'simulation') return 'live';
  if (state === 'replay' || state === 'replay_loading') return 'replay';
  return 'warn';
}

/** "verificado há {N}": segundos em passos de 5 abaixo de 60 s; depois minutos (§6.4). */
export function agoText(seconds: number): string {
  const s = Math.max(0, seconds);
  return s < 60
    ? t('status.agoSeconds', { N: Math.floor(s / 5) * 5 })
    : t('status.agoMinutes', { N: Math.floor(s / 60) });
}

interface Slots {
  before: string;
  hasT: boolean;
  mid: string;
  hasP: boolean;
  after: string;
}

function slotsOf(s: string): Slots {
  const ti = s.indexOf(T_MARK);
  if (ti < 0) {
    const pi = s.indexOf(P_MARK);
    return pi < 0
      ? { before: s, hasT: false, mid: '', hasP: false, after: '' }
      : { before: s.slice(0, pi), hasT: false, mid: '', hasP: true, after: s.slice(pi + 1) };
  }
  const rest = s.slice(ti + 1);
  const pj = rest.indexOf(P_MARK);
  return pj < 0
    ? { before: s.slice(0, ti), hasT: true, mid: rest, hasP: false, after: '' }
    : { before: s.slice(0, ti), hasT: true, mid: rest.slice(0, pj), hasP: true, after: rest.slice(pj + 1) };
}

/** Agrupa os textos de um trecho por valor: cada forma vê exatamente um deles (ou nenhum, se vazio). */
function group(slot: string, values: Record<StatusForm, string>): StatusSegment[] {
  const out: StatusSegment[] = [];
  for (const f of FORMS) {
    const text = values[f];
    if (text === '') continue;
    const hit = out.find((g) => g.text === text);
    if (hit) hit.forms += f;
    else out.push({ kind: 'text', key: `${slot}:${text}`, text, forms: f });
  }
  return out;
}

/** Trechos da parte visual: as três formas num só DOM, com uma só hora e um só percentual (CSS escolhe a forma). */
function segmentsOf(raw: Record<StatusForm, string>): StatusSegment[] {
  const slots = { l: slotsOf(raw.l), m: slotsOf(raw.m), s: slotsOf(raw.s) };
  const pick = (k: 'before' | 'mid' | 'after') => ({ l: slots.l[k], m: slots.m[k], s: slots.s[k] });
  const mask = (k: 'hasT' | 'hasP') => FORMS.filter((f) => slots[f][k]).join('');
  const out: StatusSegment[] = [...group('before', pick('before'))];
  const tMask = mask('hasT');
  if (tMask) out.push({ kind: 'time', key: 'time', text: '', forms: tMask });
  out.push(...group('mid', pick('mid')));
  const pMask = mask('hasP');
  if (pMask) out.push({ kind: 'pct', key: 'pct', text: '', forms: pMask });
  out.push(...group('after', pick('after')));
  return out;
}

/** Tudo o que o StatusIndicator mostra, a partir do estado (função pura; §6.4). */
export function statusModel(input: StatusInput): StatusModel {
  const { view, electionDate } = input;
  const state = shownState(input.connection, input.live, input.replayLoading, view !== null, input.failing);
  let timeTMin: number | null = null;
  if (state === 'replay_loading') timeTMin = input.displayTMin;
  else if (state !== 'connecting' && state !== 'failing' && view)
    timeTMin = tMinOf(view.tseHt ?? view.publishedAt, electionDate);
  const time = timeTMin === null ? null : fmtTime(timeTMin, electionDate);
  const pct =
    state === 'connecting' || state === 'failing' || state === 'replay_loading' || !view
      ? null
      : view.national.sectionsFraction;
  let ago = '';
  let checked = '';
  if (state === 'tse_stalled' && view) {
    const verified = input.verifiedAtMs ?? Date.parse(view.clocks.verified_at ?? view.clocks.collected_at);
    // há quanto tempo o TSE não manda número novo: desde a hora do último arquivo dele (a do dado na tela)
    const last = view.tseHt ? Date.parse(view.tseHt) : verified;
    ago = agoText((input.nowMs - last) / 1000);
    checked = agoText((input.nowMs - verified) / 1000);
  }
  const [kl, km, ks] = KEYS[state];
  const marked = { HHhMM: T_MARK, S: P_MARK, N: ago, V: checked };
  const raw: Record<StatusForm, string> = { l: t(kl, marked), m: t(km, marked), s: t(ks, marked) };
  const pctText = pct === null ? '' : fmtSections(pct);
  const fill = (s: string, timeText: string) => s.replace(T_MARK, timeText).replace(P_MARK, pctText);
  const forms = { l: fill(raw.l, time ?? ''), m: fill(raw.m, time ?? ''), s: fill(raw.s, time ?? '') };
  const sr = fill(raw.l, time === null ? '' : `${time}${t('status.srTimezone')}`);

  let title: string | null = null;
  if ((time !== null && view !== null) || state === 'replay_loading') {
    const replay = state === 'replay' || state === 'replay_loading';
    const lines = [t(replay ? 'status.title.replay' : 'status.title.updated', { HHhMM: time ?? '' })];
    if (!replay && view?.clocks.tse_ht) {
      lines.push(
        t('status.title.clocks', {
          HHhMM: fmtTime(tMinOf(view.clocks.tse_ht, electionDate), electionDate),
          'H:MM:SS_C': fmtClockSec(Date.parse(view.clocks.collected_at), electionDate),
          'H:MM:SS_P': fmtClockSec(Date.parse(view.clocks.published_at), electionDate),
        }),
      );
    }
    if (input.placesAhead !== null) lines.push(t('status.title.placesAhead', { S: fmtSections(input.placesAhead) }));
    title = lines.join('\n');
  }

  return {
    state,
    dot: dotOf(state),
    alert: ALERT.has(state),
    timeTMin,
    time,
    pct,
    forms,
    segments: segmentsOf(raw),
    sr,
    title,
  };
}

// ── Componente ──────────────────────────────────────────────────────────────────────────────────────────────
/** Salto de mais de 10 min no relógio dos dados: troca seca (§3.6). */
const JUMP_T_MIN = 10;

/**
 * "Atualizado às…" e estados de conexão (§6.4). `<p>` sem papel e sem `aria-live` (a região viva é única, F148): a
 * parte visual (formas longa, média e curta num só DOM, escolhidas pelo container `topbar`, pelo celular e pela TV) é
 * `aria-hidden`; a cópia `.sr-only` traz sempre a frase longa com " (horário de Brasília)".
 */
export function StatusIndicator() {
  const feed = useFeed();
  const tl = useTimeline();
  const view = useView();
  const cfg = useConfig();
  const geo = useStatics().geography;
  const failing = useFeedFailing();
  const state = shownState(feed.connection, tl.live, tl.replayLoading, view !== null, failing);
  // Só o estado "TSE sem novidade" depende do tique de 1 s (lê o sinal só nele: sem renderizar a cada segundo).
  const nowMs = state === 'tse_stalled' ? now.value.ms || Date.now() : 0;
  const m = statusModel({
    connection: feed.connection,
    live: tl.live,
    replayLoading: tl.replayLoading,
    displayTMin: tl.displayTMin,
    view,
    nowMs,
    verifiedAtMs: feed.verifiedAt,
    electionDate: cfg.election_date,
    placesAhead: placesAheadFraction(view, geo),
    failing,
  });

  const prev = useRef<number | null>(null);
  const jumped = prev.current !== null && m.timeTMin !== null && Math.abs(m.timeTMin - prev.current) > JUMP_T_MIN;
  if (m.timeTMin !== null) prev.current = m.timeTMin;
  const hidden = typeof document !== 'undefined' && document.hidden;
  const rolling = (m.state === 'live' || m.state === 'simulation') && !jumped && !hidden;

  return (
    <p
      class="status-indicator"
      data-testid="status-indicator"
      data-state={m.state}
      data-alert={m.alert ? '' : undefined}
      title={m.title ?? undefined}
    >
      <LiveDot variant={m.dot} pulseKey={m.dot === 'live' ? (m.time ?? undefined) : undefined} />
      <span class="status-indicator__text">
        <span class="status-indicator__visual" aria-hidden="true">
          {m.segments.map((seg) => {
            if (seg.kind === 'time') {
              return (
                <span key={seg.key} class="status-indicator__seg status-indicator__time" data-forms={seg.forms}>
                  <DigitRoller text={m.time ?? ''} animate={rolling} />
                </span>
              );
            }
            if (seg.kind === 'pct') {
              return (
                <b key={seg.key} class="status-indicator__seg status-indicator__pct" data-forms={seg.forms}>
                  <AnimatedNumber value={m.pct} format={fmtSections} />
                </b>
              );
            }
            return (
              <span key={seg.key} class="status-indicator__seg" data-forms={seg.forms}>
                {seg.text}
              </span>
            );
          })}
        </span>
        <span class="sr-only">{m.sr}</span>
      </span>
      {m.state === 'failing' ? (
        <a
          class="status-indicator__tse"
          data-testid="status-tse"
          href={TSE_RESULTS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('status.failing.tse')}
        </a>
      ) : null}
    </p>
  );
}
